import type { ShareFilter, SharingRelationship } from "@/features/sharing/utils/share-filter"
import type { Participant, Project } from "@/services/project-service"

import { matchesShareFilter as matchesShareFilterBase } from "@/features/sharing/utils/share-filter"

/**
 * The sharing relationship between the current user and a project, as surfaced
 * in the sidebar "Projects" section. Mirrors the chat classification
 * (`chat-sharing-state.ts`) with project-specific inputs:
 *
 * - `private` — you own it and it has no other participants. The owner is
 *   always seeded as a participant on non-legacy projects, so "no other
 *   participants" means participants minus yourself is empty.
 * - `shared_with_me` — you have a non-owner role (reader/editor/admin).
 * - `shared_by_me` — you own it and at least one other user/group participates.
 *
 * Ownership transfer ("someone made you the owner") is tracked separately via
 * the `isOwnerTransfer` flag, driven by the durable `isFormerOwner` marker the
 * backend stamps on the demoted owner — a transferred project is a special
 * case of `shared_by_me` that deserves its own marker.
 */
export type ProjectSharingState = {
  relationship: SharingRelationship
  /**
   * True when you are the current owner and ownership reached you via a
   * transfer (a former owner remains in the participant list). Note this stays
   * true after a transfer-back to the original creator — the same semantics as
   * the chat sidebar's marker, kept identical for cross-section consistency.
   */
  isOwnerTransfer: boolean
  /** For `shared_with_me`: the participant who shared the project with you (the owner). */
  sharer?: Participant
  /** For `shared_by_me`: the other participants the project is shared with (excludes you). */
  recipients: Participant[]
}

/**
 * Derive the current user's sharing relationship to a project from the data
 * already present on the list payload.
 *
 * Pure and side-effect free so it can be unit-tested and memoised cheaply.
 */
export function getProjectSharingState(
  project: Project | null | undefined,
  currentUserOid: string | undefined
): ProjectSharingState {
  if (!project) {
    return { relationship: "private", isOwnerTransfer: false, recipients: [] }
  }

  const participants = project.participants ?? []

  // Any non-owner role means the project was shared with us. The sharer shown
  // in the row is the current owner; legacy projects may lack the owner
  // participant until it is lazily backfilled, so fall back progressively.
  if (project.currentUserRole && project.currentUserRole !== "owner") {
    const sharer =
      participants.find((p) => p.role === "owner") ??
      participants.find((p) => p.oid === project.ownerUserId) ??
      participants.find((p) => p.isOriginalOwner)
    return { relationship: "shared_with_me", isOwnerTransfer: false, sharer, recipients: [] }
  }

  // Without a known current-user OID we can't reliably tell our own participant
  // entry from the others (the recipients filter would keep everyone), so fall
  // back to a conservative private state rather than misclassify an owned
  // project as shared. Non-owner roles are already handled above.
  if (!currentUserOid) {
    return { relationship: "private", isOwnerTransfer: false, recipients: [] }
  }

  // A role-less project owned by someone else (legacy payload where the
  // backend didn't compute currentUserRole) is not ours to classify — treat it
  // conservatively as private rather than claim it was shared by us.
  if (!project.currentUserRole && project.ownerUserId && project.ownerUserId !== currentUserOid) {
    return { relationship: "private", isOwnerTransfer: false, recipients: [] }
  }

  // Otherwise we own it (explicit owner role, or a legacy project with no role
  // whose ownerUserId matches us).
  const recipients = participants.filter((p) => p.oid !== currentUserOid)

  // Ownership was transferred to us when we're the current owner and a former
  // owner remains in the participant list. The backend flags the demoted former
  // owner with isFormerOwner on transfer, which is the only reliable signal:
  // after a transfer ownerUserId is reassigned to the new owner.
  const isOwnerTransfer =
    project.currentUserRole === "owner" && participants.some((p) => p.isFormerOwner)

  const relationship: SharingRelationship = recipients.length > 0 ? "shared_by_me" : "private"

  return { relationship, isOwnerTransfer, recipients }
}

/** Whether a project with the given sharing state should appear under the active filter. */
export function matchesProjectShareFilter(
  state: ProjectSharingState,
  filter: ShareFilter
): boolean {
  return matchesShareFilterBase(state.relationship, filter)
}
